import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import ArtworkImage from "@/components/ArtworkImage";
import { artText } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import Seo from "@/components/Seo";

export default function Portfolio() {
  const { artworks, loading } = useStore();
  const { t, lang } = useLang();

  return (
    <div data-testid="portfolio-page" className="mx-auto max-w-7xl px-6 pb-28 pt-32 sm:px-10 lg:px-16">
      <Seo title={t("seo.portfolioTitle")} description={t("seo.portfolio")} />
      <Reveal>
        <p className="eyebrow">{t("portfolio.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-4xl font-light text-bone sm:text-5xl lg:text-6xl">
          {t("portfolio.title")}
        </h1>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-bone-dim/80 sm:text-base">
          {t("portfolio.intro")}
        </p>
      </Reveal>

      <div className="mt-20 space-y-28">
        {artworks.map((art, i) => (
          <Reveal key={art.artwork_id}>
            <article
              data-testid={`portfolio-case-${art.artwork_id}`}
              className={`grid items-center gap-12 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <Link to={`/shop/${art.artwork_id}`} className="group block">
                <ArtworkImage art={art} ratio="aspect-[5/4]" />
              </Link>
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-brass">
                  {t("portfolio.study")} {String(i + 1).padStart(2, "0")} — {art.dwg_no}
                </p>
                <h2 className="mt-5 font-serif text-3xl font-light text-bone sm:text-4xl">{artText(art, "title", lang)}</h2>
                <p className="mt-5 text-sm leading-relaxed text-bone-dim/85 sm:text-base">{artText(art, "story", lang)}</p>
                <div className="mt-7 flex flex-wrap gap-2">
                  {[art.dimensions, artText(art, "medium", lang), art.year].map((chip) => (
                    <span key={chip} className="border border-bone/10 px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] text-bone-dim">
                      {chip}
                    </span>
                  ))}
                </div>
                <Link
                  data-testid={`portfolio-view-${art.artwork_id}`}
                  to={`/shop/${art.artwork_id}`}
                  className="mt-8 inline-block font-mono text-[10px] tracking-[0.3em] text-brass transition-transform duration-300 hover:translate-x-1"
                >
                  {t("portfolio.view")}
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      {!loading && artworks.length === 0 && (
        <p className="mt-20 text-center font-mono text-[11px] tracking-[0.3em] text-bone-muted">
          {t("portfolio.empty")}
        </p>
      )}
    </div>
  );
}
